import { useEffect, useMemo, useState } from 'react';
import {
  CopyPlus,
  Eye,
  ListPlus,
  RefreshCcw,
  Save,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'react-toastify';
import TenantSiteImageUploadField from '../../components/admin/TenantSiteImageUploadField';
import { defaultAboutPageContent, mergeAboutPageContent } from '../../content/defaultTenantSitePages';
import { useAuth } from '../../context/AuthContext';
import { tenantSiteImageAssetConfigs } from '../../services/TenantSiteImageStorageService';
import { TenantSitePagesService } from '../../services/TenantSitePagesService';
import type { TenantAboutPageContent, TenantSiteAsset, TenantSiteTeamMember } from '../../types/site-pages';

const createEmptyAsset = (): TenantSiteAsset => ({
  provider: 'firebase-storage',
  storagePath: '',
  downloadUrl: '',
  alt: '',
});

const createEmptyTeamMember = (): TenantSiteTeamMember => ({
  id: `member-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  role: '',
  credentials: [''],
  bio: '',
  photo: createEmptyAsset(),
  sortOrder: 999,
  active: true,
});

const cloneAboutContent = (content: TenantAboutPageContent): TenantAboutPageContent =>
  JSON.parse(JSON.stringify(content)) as TenantAboutPageContent;

const AboutPageEditor = () => {
  const { user, selectedClient } = useAuth();
  const clientId = selectedClient?.clientId ?? '';
  const [formData, setFormData] = useState<TenantAboutPageContent>(() =>
    cloneAboutContent(defaultAboutPageContent),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedOverride, setHasSavedOverride] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const sortedTeamMembers = useMemo(
    () =>
      [...(formData.team?.members ?? [])].sort(
        (left, right) =>
          (left.sortOrder ?? Number.MAX_SAFE_INTEGER) -
          (right.sortOrder ?? Number.MAX_SAFE_INTEGER),
      ),
    [formData.team?.members],
  );

  const loadContent = async () => {
    if (!clientId) {
      setFormData(cloneAboutContent(defaultAboutPageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const pageRecord = await TenantSitePagesService.getByClientId(clientId, 'about');
      const merged = mergeAboutPageContent(pageRecord?.contentJson ?? null);

      setFormData(cloneAboutContent(merged));
      setHasSavedOverride(Boolean(pageRecord));
      setLastUpdatedAt(pageRecord?.updatedAt ?? null);
    } catch (error) {
      console.error('Erro ao carregar conteúdo da página sobre:', error);
      toast.error('Não foi possível carregar o conteúdo da página sobre.');
      setFormData(cloneAboutContent(defaultAboutPageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadContent();
  }, [clientId]);

  const updateTopLevelSection = <
    TSection extends keyof TenantAboutPageContent,
    TField extends keyof NonNullable<TenantAboutPageContent[TSection]>,
  >(
    section: TSection,
    field: TField,
    value: NonNullable<TenantAboutPageContent[TSection]>[TField],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] ?? {}),
        [field]: value,
      },
    }));
  };

  const updateStringList = (
    section: 'values' | 'differentials',
    index: number,
    value: string,
  ) => {
    const currentItems = [...(formData[section]?.items ?? [])];
    currentItems[index] = value;

    updateTopLevelSection(section, 'items', currentItems);
  };

  const addStringListItem = (section: 'values' | 'differentials') => {
    const currentItems = [...(formData[section]?.items ?? [])];
    currentItems.push('');
    updateTopLevelSection(section, 'items', currentItems);
  };

  const removeStringListItem = (section: 'values' | 'differentials', index: number) => {
    const currentItems = [...(formData[section]?.items ?? [])];
    currentItems.splice(index, 1);
    updateTopLevelSection(section, 'items', currentItems);
  };

  const updateHistoryParagraph = (index: number, value: string) => {
    const paragraphs = [...(formData.history?.paragraphs ?? [])];
    paragraphs[index] = value;
    updateTopLevelSection('history', 'paragraphs', paragraphs);
  };

  const addHistoryParagraph = () => {
    const paragraphs = [...(formData.history?.paragraphs ?? [])];
    paragraphs.push('');
    updateTopLevelSection('history', 'paragraphs', paragraphs);
  };

  const removeHistoryParagraph = (index: number) => {
    const paragraphs = [...(formData.history?.paragraphs ?? [])];
    paragraphs.splice(index, 1);
    updateTopLevelSection('history', 'paragraphs', paragraphs);
  };

  const updateTeamMember = (
    memberId: string,
    updater: (member: TenantSiteTeamMember) => TenantSiteTeamMember,
  ) => {
    const members = [...(formData.team?.members ?? [])].map((member) =>
      member.id === memberId ? updater(member) : member,
    );

    updateTopLevelSection('team', 'members', members);
  };

  const addTeamMember = () => {
    const members = [...(formData.team?.members ?? [])];
    members.push(createEmptyTeamMember());
    updateTopLevelSection('team', 'members', members);
  };

  const removeTeamMember = (memberId: string) => {
    const members = [...(formData.team?.members ?? [])].filter((member) => member.id !== memberId);
    updateTopLevelSection('team', 'members', members);
  };

  const addCredential = (memberId: string) => {
    updateTeamMember(memberId, (member) => ({
      ...member,
      credentials: [...member.credentials, ''],
    }));
  };

  const updateCredential = (memberId: string, index: number, value: string) => {
    updateTeamMember(memberId, (member) => {
      const credentials = [...member.credentials];
      credentials[index] = value;
      return {
        ...member,
        credentials,
      };
    });
  };

  const removeCredential = (memberId: string, index: number) => {
    updateTeamMember(memberId, (member) => {
      const credentials = [...member.credentials];
      credentials.splice(index, 1);
      return {
        ...member,
        credentials: credentials.length ? credentials : [''],
      };
    });
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clientId || !user?.userId) {
      toast.error('Selecione um tenant antes de salvar.');
      return;
    }

    setIsSaving(true);

    try {
      const saved = await TenantSitePagesService.upsertForClient(
        clientId,
        'about',
        formData,
        user.userId,
      );

      setFormData(cloneAboutContent(mergeAboutPageContent(saved.contentJson)));
      setHasSavedOverride(true);
      setLastUpdatedAt(saved.updatedAt);
      toast.success('Página sobre atualizada com sucesso.');
    } catch (error) {
      console.error('Erro ao salvar página sobre:', error);
      toast.error('Não foi possível salvar a página sobre.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setFormData(cloneAboutContent(defaultAboutPageContent));
  };

  const handleDeleteOverride = async () => {
    if (!clientId) {
      toast.error('Selecione um tenant antes de remover o override.');
      return;
    }

    try {
      setIsSaving(true);
      await TenantSitePagesService.deleteForClient(clientId, 'about');
      toast.success('As personalizações foram removidas e a versão padrão da página foi restaurada.');
      await loadContent();
    } catch (error) {
      console.error('Erro ao remover override da página sobre:', error);
      toast.error('Não foi possível remover o override da página sobre.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedClient) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Selecione um tenant para editar a página sobre.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-700">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Página Sobre</h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Edite o conteúdo institucional exibido em <code>/sobre</code> para este tenant.
                As fotos da equipe são enviadas diretamente para o Firebase Storage.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-800">Tenant:</span> {selectedClient.clientName}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Origem atual:</span>{' '}
              {hasSavedOverride ? 'Conteúdo salvo no banco' : 'Fallback do sistema'}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Última atualização:</span>{' '}
              {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString('pt-BR') : 'Não salvo'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Estrutura principal</h3>
                <p className="text-sm text-slate-500">
                  Título, intro, missão, blocos e CTA principal.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void loadContent()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <RefreshCcw className="h-4 w-4" />
                Recarregar
              </button>
            </div>

            <div className="grid gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Título da página</label>
                <input
                  type="text"
                  value={formData.hero?.title ?? ''}
                  onChange={(event) => updateTopLevelSection('hero', 'title', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Texto de introdução</label>
                <textarea
                  rows={4}
                  value={formData.intro?.body ?? ''}
                  onChange={(event) => updateTopLevelSection('intro', 'body', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Título da missão</label>
                <input
                  type="text"
                  value={formData.mission?.title ?? ''}
                  onChange={(event) => updateTopLevelSection('mission', 'title', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Texto da missão</label>
                <textarea
                  rows={4}
                  value={formData.mission?.body ?? ''}
                  onChange={(event) => updateTopLevelSection('mission', 'body', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Valores e diferenciais</h3>
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              {(['values', 'differentials'] as const).map((section) => (
                <div key={section} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <input
                      type="text"
                      value={formData[section]?.title ?? ''}
                      onChange={(event) => updateTopLevelSection(section, 'title', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={() => addStringListItem(section)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
                    >
                      <ListPlus className="h-4 w-4" />
                      Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(formData[section]?.items ?? []).map((item, index) => (
                      <div key={`${section}-${index}`} className="flex items-start gap-2">
                        <textarea
                          rows={2}
                          value={item}
                          onChange={(event) => updateStringList(section, index, event.target.value)}
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeStringListItem(section, index)}
                          className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">História</h3>
                <p className="text-sm text-slate-500">Parágrafos livres na ordem em que aparecem na página.</p>
              </div>
              <button
                type="button"
                onClick={addHistoryParagraph}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <CopyPlus className="h-4 w-4" />
                Parágrafo
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={formData.history?.title ?? ''}
                onChange={(event) => updateTopLevelSection('history', 'title', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 font-semibold focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />

              {(formData.history?.paragraphs ?? []).map((paragraph, index) => (
                <div key={`history-${index}`} className="flex items-start gap-2">
                  <textarea
                    rows={4}
                    value={paragraph}
                    onChange={(event) => updateHistoryParagraph(index, event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeHistoryParagraph(index)}
                    className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Equipe</h3>
                <p className="text-sm text-slate-500">
                  As fotos da equipe ficam isoladas por tenant no Firebase Storage.
                </p>
              </div>
              <button
                type="button"
                onClick={addTeamMember}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <Users className="h-4 w-4" />
                Membro
              </button>
            </div>

            <div className="space-y-5">
              <input
                type="text"
                value={formData.team?.title ?? ''}
                onChange={(event) => updateTopLevelSection('team', 'title', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 font-semibold focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />

              {sortedTeamMembers.map((member) => (
                <div key={member.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{member.name || 'Novo membro'}</p>
                      <p className="text-xs text-slate-500">ID: {member.id}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTeamMember(member.id)}
                      className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <input
                      type="text"
                      value={member.name}
                      onChange={(event) =>
                        updateTeamMember(member.id, (current) => ({ ...current, name: event.target.value }))
                      }
                      placeholder="Nome"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <input
                      type="text"
                      value={member.role}
                      onChange={(event) =>
                        updateTeamMember(member.id, (current) => ({ ...current, role: event.target.value }))
                      }
                      placeholder="Cargo"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <input
                      type="number"
                      value={member.sortOrder ?? 0}
                      onChange={(event) =>
                        updateTeamMember(member.id, (current) => ({
                          ...current,
                          sortOrder: Number(event.target.value) || 0,
                        }))
                      }
                      placeholder="Ordem"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <label className="flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={member.active !== false}
                        onChange={(event) =>
                          updateTeamMember(member.id, (current) => ({
                            ...current,
                            active: event.target.checked,
                          }))
                        }
                      />
                      Exibir na página
                    </label>
                    <textarea
                      rows={3}
                      value={member.bio ?? ''}
                      onChange={(event) =>
                        updateTeamMember(member.id, (current) => ({ ...current, bio: event.target.value }))
                      }
                      placeholder="Bio curta"
                      className="md:col-span-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <TenantSiteImageUploadField
                        asset={member.photo ?? null}
                        assetConfig={tenantSiteImageAssetConfigs.aboutTeamMember}
                        assetPath={`team/${member.id}`}
                        clientId={clientId}
                        label={`Foto de ${member.name || 'membro da equipe'}`}
                        pageKey="about"
                        onUploaded={(asset) =>
                          updateTeamMember(member.id, (current) => ({
                            ...current,
                            photo: {
                              ...(current.photo ?? createEmptyAsset()),
                              ...asset,
                              alt: current.photo?.alt || asset.alt || '',
                            },
                          }))
                        }
                        onClear={() =>
                          updateTeamMember(member.id, (current) => ({
                            ...current,
                            photo: createEmptyAsset(),
                          }))
                        }
                      />
                    </div>

                    <input
                      type="text"
                      value={member.photo?.alt ?? ''}
                      onChange={(event) =>
                        updateTeamMember(member.id, (current) => ({
                          ...current,
                          photo: {
                            ...(current.photo ?? createEmptyAsset()),
                            alt: event.target.value,
                          },
                        }))
                      }
                      placeholder="Texto alternativo da foto"
                      className="md:col-span-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-800">Credenciais</p>
                      <button
                        type="button"
                        onClick={() => addCredential(member.id)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                      >
                        <ListPlus className="h-4 w-4" />
                        Credencial
                      </button>
                    </div>

                    <div className="space-y-3">
                      {member.credentials.map((credential, index) => (
                        <div key={`${member.id}-credential-${index}`} className="flex items-start gap-2">
                          <textarea
                            rows={2}
                            value={credential}
                            onChange={(event) => updateCredential(member.id, index, event.target.value)}
                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <button
                            type="button"
                            onClick={() => removeCredential(member.id, index)}
                            className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">CTA final</h3>
            <div className="mt-5 grid gap-4">
              <input
                type="text"
                value={formData.cta?.title ?? ''}
                onChange={(event) => updateTopLevelSection('cta', 'title', event.target.value)}
                placeholder="Título do CTA"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={3}
                value={formData.cta?.body ?? ''}
                onChange={(event) => updateTopLevelSection('cta', 'body', event.target.value)}
                placeholder="Texto do CTA"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateTopLevelSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { path: '/agendar' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Texto do botão"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateTopLevelSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { label: 'Agendar Agora' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/agendar"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleResetToDefault}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCcw className="h-4 w-4" />
              Carregar padrão
            </button>

            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar página sobre'}
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-slate-500" />
              <h3 className="text-lg font-semibold text-slate-900">Resumo rápido</h3>
            </div>

            <div className="mt-5 space-y-3 text-sm text-slate-600">
              <p>
                <span className="font-semibold text-slate-800">Título:</span> {formData.hero?.title || 'Não preenchido'}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Valores:</span> {formData.values?.items?.length ?? 0} itens
              </p>
              <p>
                <span className="font-semibold text-slate-800">Diferenciais:</span> {formData.differentials?.items?.length ?? 0} itens
              </p>
              <p>
                <span className="font-semibold text-slate-800">Parágrafos da história:</span> {formData.history?.paragraphs?.length ?? 0}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Equipe:</span> {formData.team?.members?.length ?? 0} membros
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 shadow-sm">
            <p className="font-semibold">Escopo desta etapa</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Editor completo para o conteúdo textual da página sobre.</li>
              <li>Fotos da equipe com upload real para Firebase Storage.</li>
              <li>Remoção e substituição de imagem no mesmo fluxo de edição.</li>
              <li>Possibilidade de remover o override e voltar ao fallback atual.</li>
            </ul>
          </div>

          {hasSavedOverride && (
            <button
              type="button"
              onClick={() => void handleDeleteOverride()}
              disabled={isSaving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              Remover override salvo
            </button>
          )}

          {isLoading && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              Carregando conteúdo da página sobre...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutPageEditor;
